import { type CommunityChannelData } from '@ccm-innovation/member-center-bff-sdk-axios';
import * as Tooltip from '@radix-ui/react-tooltip';
import { type FunctionComponent, useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';
import { getCommunityGroupListSlice } from '../../../core/async-action/get-community-group-list/get-community-group-list.slice';
import { streamSlice } from '../../../core/stream/stream.slice';
import { useEnvironment } from '../../../hooks/useEnvironment';
import { useTranslation } from '../../../i18n/resources';
import { LogoutIcon } from '../../../icons/LogoutIcon';
import { MemberCenterIcon } from '../../../icons/MemberCenterIcon';
import { SettingsIcon } from '../../../icons/SettingsIcon';
import { GroupManagementAlertDialog } from '../../GroupManagementAlertDialog/GroupManagementAlertDialog';
import styles from './SidebarMenu.module.css';

export type SidebarMenuProps = {
  memberChannelData: CommunityChannelData[] | null;
};

export const SidebarMenu: FunctionComponent<SidebarMenuProps> = ({
  memberChannelData,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const environment = useEnvironment();
  const dispatch = useDispatch();
  const [showGroupsModal, setShowGroupsModal] = useState<boolean>(false);

  const handleLogout = useCallback((): void => {
    void navigate('/logout');
  }, [navigate]);

  return (
    <>
      <a
        className={styles.menuIcon}
        href={environment.VITE_APP_PRAYER_STREAM_MEMBER_CENTER_URL}
      >
        <Tooltip.Provider>
          <Tooltip.Root delayDuration={0}>
            <Tooltip.Trigger asChild>
              <MemberCenterIcon />
            </Tooltip.Trigger>
            <Tooltip.Portal>
              <Tooltip.Content
                className={styles.tooltip}
                side={'right'}
                sideOffset={10}
              >
                {t('prayerStream', 'go_to_member_center')}
                <Tooltip.Arrow
                  className={styles.tooltipArrow}
                  height={10}
                  width={15}
                />
              </Tooltip.Content>
            </Tooltip.Portal>
          </Tooltip.Root>
        </Tooltip.Provider>
      </a>
      <div
        className={styles.menuIcon}
        onClick={() => {
          dispatch(
            getCommunityGroupListSlice.actions.getCommunityGroupListRequested(),
          );
          setShowGroupsModal(true);
        }}
      >
        <Tooltip.Provider>
          <Tooltip.Root delayDuration={0}>
            <Tooltip.Trigger asChild>
              <SettingsIcon />
            </Tooltip.Trigger>
            <Tooltip.Portal>
              <Tooltip.Content
                className={styles.tooltip}
                side={'right'}
                sideOffset={10}
              >
                {t('groupManagement', 'manage_my_groups')}
                <Tooltip.Arrow
                  className={styles.tooltipArrow}
                  height={10}
                  width={15}
                />
              </Tooltip.Content>
            </Tooltip.Portal>
          </Tooltip.Root>
        </Tooltip.Provider>
      </div>

      <div className={styles.menuIcon} onClick={handleLogout}>
        <Tooltip.Provider>
          <Tooltip.Root delayDuration={0}>
            <Tooltip.Trigger asChild>
              <LogoutIcon />
            </Tooltip.Trigger>
            <Tooltip.Portal>
              <Tooltip.Content
                className={styles.tooltip}
                side={'right'}
                sideOffset={10}
              >
                {t('common', 'log_out')}
                <Tooltip.Arrow
                  className={styles.tooltipArrow}
                  height={10}
                  width={15}
                />
              </Tooltip.Content>
            </Tooltip.Portal>
          </Tooltip.Root>
        </Tooltip.Provider>
      </div>
      {showGroupsModal && !!memberChannelData && (
        <GroupManagementAlertDialog
          memberChannelData={memberChannelData}
          onboarding={false}
          onClose={() => {
            setShowGroupsModal(false);
            dispatch(streamSlice.actions.setCommunityGroupList(null));
          }}
        />
      )}
    </>
  );
};
